<template>
  <div class="has-float-label custom-input" :class="rootClasses">
    <b-input
      class="form-control"
      :id="resolvedInputId"
      :size="size"
      :placeholder="resolvedPlaceholder"
      :type="inputType"
      :name="name"
      :class="cssClass"
      :value="value"
      :readonly="readonly"
      :disabled="disabled"
      :maxlength="maxlength"
      @input="(e) => $emit('input', e)"
      @keydown="handleKeydown"
      @paste="handlePaste"
    />
    <label :for="resolvedInputId">{{ labelText }}</label>
  </div>
</template>
<script>
export default {
  name: "input",
  props: {
    inputType: {
      type: String,
      default: "text",
    },
    name: {
      type: String,
      default: "textbox",
    },
    cssClass: {
      type: String,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    labelText: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      required: false,
      default: "",
    },
    inputId: {
      type: String,
      required: false,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "lg",
    },
    margin: {
      type: Boolean,
      default: true,
    },
    maxlength: {
      type: [Number, String],
      required: false,
      default: undefined,
    },
    digitsOnly: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "common",
    },
  },
  data() {
    return {
      generatedInputId: `custom-input-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
    };
  },
  computed: {
    rootClasses() {
      return {
        "form-group": this.margin,
        "m-0": this.variant === "common-model",
        "custom-input--common": this.variant === "common",
        "custom-input--common-model": this.variant === "common-model",
        "custom-input--form-elements": this.variant === "form-elements",
      };
    },
    resolvedPlaceholder() {
      if (this.placeholder !== undefined) {
        return this.placeholder;
      }
      return this.variant === "form-elements" ? "" : " ";
    },
    resolvedInputId() {
      if (this.inputId) {
        return this.inputId;
      }
      return this.variant === "common" ? this.generatedInputId : undefined;
    },
  },
  methods: {
    handleKeydown(e) {
      if (!this.digitsOnly) {
        return;
      }

      const key = e.key;
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];
      const isAllowedKey = allowedKeys.includes(key);
      const isDigit = /^\d$/.test(key);
      const isCtrlA = e.ctrlKey && key === "a";
      const isCtrlC = e.ctrlKey && key === "c";
      const isCtrlV = e.ctrlKey && key === "v";
      const isCtrlX = e.ctrlKey && key === "x";

      if (
        !isAllowedKey &&
        !isDigit &&
        !isCtrlA &&
        !isCtrlC &&
        !isCtrlV &&
        !isCtrlX
      ) {
        e.preventDefault();
      }
    },
    handlePaste(e) {
      if (!this.digitsOnly) {
        return;
      }

      e.preventDefault();
      const paste = (e.clipboardData || window.clipboardData).getData("text");
      const digitsOnlyValue = paste.replace(/\D/g, "");
      const maxLength = this.maxlength ? parseInt(this.maxlength, 10) : 12;
      const filtered = digitsOnlyValue.slice(0, maxLength);

      if (filtered) {
        const currentValue = this.value || "";
        const newValue = `${currentValue}${filtered}`;
        const finalValue = newValue.replace(/\D/g, "").slice(0, maxLength);
        this.$emit("input", { target: { value: finalValue } });
      }
    },
  },
};
</script>

<style scoped>
@import "../../assets/css/bootstrap-float-label.css";

b-input::-webkit-outer-spin-button,
b-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.custom-input .form-control {
  color: rgb(0, 0, 0);
  box-shadow: none !important;
  font-family: "Lato";
}

.custom-input--common .form-control {
  border-radius: 0rem;
  height: 39px !important;
  border: 1.5px solid #dee2e6;
}

.custom-input--common .form-control:focus {
  border-color: #030303;
}

.custom-input--common-model .form-control {
  border-radius: 0rem;
  height: 36px !important;
  border: 1px solid #dee2e6;
}

.custom-input--common-model .form-control:focus,
.custom-input--form-elements .form-control:focus {
  border-color: #dee2e6;
}

.custom-input--form-elements .form-control {
  border-radius: var(--radius-sm);
  height: 39px !important;
  border: 1px solid #dee2e6;
}

.custom-input--form-elements .form-control-lg {
  font-size: 130%;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}

@media (max-width: 576px) {
  .custom-input--form-elements .form-control {
    font-size: 16px;
  }
}
</style>
